import React, { FC } from "react";
import { AllTimeBestQuarter } from "@shared/models";
import { getGbFormattedShortDate } from "@shared/utils";

import { bestQuarterCopy } from "../bestQuarterCopy";
import { ordinalRank } from "../ordinalRank";
import "./index.scss";

interface YourAllTimeCardProps {
  /** `SparksSummary.lifetimeEarned` -- `null` only while that fetch has not yet settled, never a
   *  placeholder for "zero recognitions" (0 is a real, correct lifetime figure). */
  lifetimeEarned: number | null;
  /** `viewer.rank` over `board=received&period=All-time` -- ALWAYS this fixed period, regardless of
   *  whichever period the tab's own dropdown is on. `null` when the viewer has no score at all in
   *  ALL_TIME. */
  allTimeRank: number | null;
  /** The viewer's own highest-scoring quarter ever, or `null` for zero approved received recognitions. */
  bestQuarter: AllTimeBestQuarter | null;
  /** ISO instant of the viewer's first approved received recognition, or `null` for none. */
  firstSparkReceivedAt: string | null;
}

/**
 * L8's "Your all-time" rail card (923-core-context context/engineering/spark-rewards/phase-51-leaderboard-to-design.md) -- right rail, above the existing
 * "Value crowns" card. Purely presentational: every figure arrives pre-computed/pre-fetched, and this
 * component's only job is to format and lay them out (`bestQuarterCopy`/`ordinalRank`/
 * `getGbFormattedShortDate` do the formatting; none of it is computed here).
 *
 * "never resets" is static copy, not derived from any field -- unlike the tab's own reset line (L7,
 * `leaderboardResetCopy.ts`), this card's own figures are all-time and never roll over.
 */
const YourAllTimeCard: FC<YourAllTimeCardProps> = (props) => {
  const { lifetimeEarned, allTimeRank, bestQuarter, firstSparkReceivedAt } = props;

  // Handled here, before calling the shared formatter, so THIS card renders one placeholder glyph
  // throughout ("—", matching ordinalRank/bestQuarterCopy's own empty marker) rather than falling
  // through to getGbFormattedShortDate's own "-" for null. Deliberately NOT changed at the shared
  // formatter itself -- other screens already depend on ITS default, and that is exactly the kind of
  // shared-default edit that would silently restyle unrelated pages for this one card's own taste.
  const firstSparkReceivedLabel = firstSparkReceivedAt === null ? "—" : getGbFormattedShortDate(firstSparkReceivedAt);

  return (
    <div className="your-all-time-card">
      <div className="your-all-time-card-header">
        <div className="your-all-time-card-title">Your all-time</div>
        <div className="your-all-time-card-never-resets">never resets</div>
      </div>
      <div className="your-all-time-card-lifetime">
        <span className="your-all-time-card-lifetime-value">{lifetimeEarned ?? "—"}</span>
        <span className="your-all-time-card-lifetime-label">lifetime Sparks</span>
      </div>
      <div className="your-all-time-card-row">
        <span className="your-all-time-card-row-label">All-time rank</span>
        <span className="your-all-time-card-row-value">{ordinalRank(allTimeRank)}</span>
      </div>
      <div className="your-all-time-card-row">
        <span className="your-all-time-card-row-label">Best quarter</span>
        <span className="your-all-time-card-row-value">{bestQuarterCopy(bestQuarter)}</span>
      </div>
      <div className="your-all-time-card-row">
        <span className="your-all-time-card-row-label">First Spark received</span>
        <span className="your-all-time-card-row-value">{firstSparkReceivedLabel}</span>
      </div>
    </div>
  );
};

export default YourAllTimeCard;
